import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Shield, ArrowRight, Heart, Truck, RotateCcw, ShoppingBag, Pencil, Clock, Leaf, Ruler, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import ProductCardV2 from '../components/ProductCardV2';
import { toast } from 'sonner';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { absoluteUrl, useSEO } from '../utils/seo';
import { CUSTOM_ORDERS_ENABLED, DEFAULT_CUSTOM_PRICE, FREE_SHIPPING_THRESHOLD, publicDescription } from '../lib/shop';
import { readResponse } from '../utils/checkout';
import { characterSlug, withCharacterName } from '../lib/characters';
import { useCopy, useLocale } from '../i18n/LocaleContext';
import ProductGallery from '../components/ProductMedia';
import {productMedia} from '../utils/productMedia';
import './ProductAtelier.css';
const API = (process.env.REACT_APP_BACKEND_URL || '');

const COPY = {
  fr: {
    seoFallbackTitle: 'Produit — Le Placard à Doudous', seoFallbackDescription: 'Marque-page en tissu cousu main en France, en minkee ultra-doux.',
    unavailableProduct: 'Produit indisponible', added: name => `${name} ajouté au placard`,
    loginForWishlist: 'Connectez-vous pour gérer votre wishlist', wishlistError: 'La wishlist n’a pas été mise à jour',
    wishlistRemoved: 'Retiré de la wishlist', wishlistAdded: 'Ajouté à la wishlist', loadingProduct: 'Chargement du produit',
    temporarilyUnavailable: 'Cet ami tout doux est momentanément indisponible', lost: 'Cet ami tout doux s’est égaré…', retry: 'Réessayer', backToShop: 'Retour à la boutique',
    adopted: 'Déjà adoptée', toOrder: 'Sur commande', available: 'Pièce disponible', unavailable: 'Pièce indisponible', adopt: 'Adopter cette pièce',
    breadcrumb: 'Fil d’Ariane', shop: 'Boutique', removeWishlist: 'Retirer de la wishlist', addWishlist: 'Ajouter à la wishlist',
    subtitle: 'Marque-page en Minky, cousu main en France', handmade: 'fait main',
    adoptedNote: 'Cette pièce a trouvé sa lectrice ou son lecteur. Elle vit désormais entre les pages de quelqu’un d’autre.',
    customSection: 'Commander sur mesure', customTitle: name => `Le même ${name}, à votre façon`, customTitleMobile: 'À composer', allIncluded: ' — forfait tout compris',
    customDescription: name => `${name}, avec vos tissus et votre fil de couture. Son visage brodé reste celui du modèle.`,
    customOptions: 'Tissus et détails au choix', customOptionLines: ['Couleurs du Minky, tissu du dos et fil au choix', 'Ruban en Liberty indépendant du dos'],
    designMine: 'Composer le mien', customDelivery: free => `Cousue main en 10 à 15 jours${free ? ' · Livraison offerte' : ''}`,
    materials: 'Matières', care: 'Entretien', madeIn: 'Fabrication', dimensions: 'Dimensions', piece: 'Pièce', uniquePiece: 'Pièce unique',
    delivery: 'Livraison', deliveryText: threshold => `Livraison suivie en France et dans le monde, offerte dès ${threshold}.`,
    returnsCustom: '14 jours pour changer d’avis sur une pièce de la collection. Le sur mesure n’est ni repris ni échangé.', returns: '14 jours pour changer d’avis.',
    secure: 'Paiement sécurisé. Emballage papier de soie et mot d’atelier.',
    related: 'Ses compagnons de lecture', wholeCollection: 'Découvrir toute la collection',
  },
  en: {
    seoFallbackTitle: 'Product — Le Placard à Doudous', seoFallbackDescription: 'Fabric bookmark sewn by hand in France, in ultra-soft minky.',
    unavailableProduct: 'Product unavailable', added: name => `${name} has been added to your cupboard`,
    loginForWishlist: 'Log in to manage your wishlist', wishlistError: 'Your wishlist couldn’t be updated',
    wishlistRemoved: 'Removed from your wishlist', wishlistAdded: 'Added to your wishlist', loadingProduct: 'Loading the product',
    temporarilyUnavailable: 'This cuddly friend is temporarily unavailable', lost: 'This cuddly friend has wandered off…', retry: 'Try again', backToShop: 'Back to the shop',
    adopted: 'Already adopted', toOrder: 'Made to order', available: 'Available', unavailable: 'Unavailable', adopt: 'Adopt this piece',
    breadcrumb: 'Breadcrumb', shop: 'Shop', removeWishlist: 'Remove from your wishlist', addWishlist: 'Add to your wishlist',
    subtitle: 'Minky bookmark, sewn by hand in France', handmade: 'handmade',
    adoptedNote: 'This piece has found its reader. It now lives between someone else’s pages.',
    customSection: 'Order made to measure', customTitle: name => `The same ${name}, your way`, customTitleMobile: 'Made to order', allIncluded: ' — all included',
    customDescription: name => `${name}, with your fabrics and your sewing thread. The embroidered face stays the same as the original.`,
    customOptions: 'Fabrics and details to choose', customOptionLines: ['Minky colours, back fabric and thread of your choice', 'Liberty ribbon, chosen separately from the back'],
    designMine: 'Design mine', customDelivery: free => `Sewn by hand in 10 to 15 days${free ? ' · Free shipping' : ''}`,
    materials: 'Materials', care: 'Care', madeIn: 'Made in', dimensions: 'Dimensions', piece: 'Piece', uniquePiece: 'One of a kind',
    delivery: 'Shipping', deliveryText: threshold => `Tracked shipping to France and worldwide, free from ${threshold}.`,
    returnsCustom: '14 days to change your mind on a piece from the collection. Made-to-order pieces can’t be returned or exchanged.', returns: '14 days to change your mind.',
    secure: 'Secure payment. Wrapped in tissue paper, with a note from the workshop.',
    related: 'Its reading companions', wholeCollection: 'Discover the whole collection',
  },
};

export default function Product() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const [selectedColor, setSelectedColor] = useState('');
  const { addItem } = useCart();
  const { user, token, refreshUser } = useAuth();
  const c = useCopy(COPY);
  const { locale, lp, price: formatPrice } = useLocale();
  useSEO(
    product ? `${product.name} — Le Placard à Doudous` : c.seoFallbackTitle,
    product?.tagline || c.seoFallbackDescription,
    lp(`/produit/${slug}`)
  );

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setProduct(null);
    setLoadError(false);
    setSelectedColor('');
    Promise.all([
      fetch(`${API}/api/products/${slug}`, { signal: controller.signal }).then(r => r.status === 404 ? null : readResponse(r, c.unavailableProduct)),
      // Catalogue complet trié « featured », puis 3 autres modèles (pas seulement featured=true)
      fetch(`${API}/api/products?sort=featured`, { signal: controller.signal }).then(r => r.ok ? r.json() : []).catch(() => [])
    ]).then(([prod, related]) => {
      if (controller.signal.aborted) return;
      setProduct(withCharacterName(prod, locale));
      if (prod?.colors?.[0]) setSelectedColor(prod.colors[0].name);
      // Les autres pièces du même personnage d'abord, puis le reste du catalogue.
      const others = Array.isArray(related) ? related.filter(r => r.slug !== prod?.slug) : [];
      const sameCharacter = r => characterSlug(r.slug) === characterSlug(prod?.slug);
      setRelatedProducts([...others.filter(sameCharacter), ...others.filter(r => !sameCharacter(r))].slice(0, 3));
    }).catch(() => { if (!controller.signal.aborted) setLoadError(true); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, retry, locale]);

  const handleAddToCart = () => {
    if (!product || product.made_to_order || product.status === 'adopted' || !(product.stock > 0)) return;
    // Une pièce du registre : il n'en existe qu'une, donc jamais plus de 1.
    addItem(product, selectedColor, 1, {image:productMedia(product)[0]?.src});
    toast.success(c.added(product.name));
  };

  const handleWishlist = async () => {
    if (!user) { toast.error(c.loginForWishlist); navigate(lp('/connexion')); return; }
    if (wishlistLoading) return;
    setWishlistLoading(true);
    try {
      const response = await fetch(`${API}/api/user/wishlist/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ product_slug: slug })
      });
      await readResponse(response, c.wishlistError);
      await refreshUser();
      toast.success(isInWishlist ? c.wishlistRemoved : c.wishlistAdded);
    } catch { toast.error(c.wishlistError); }
    finally { setWishlistLoading(false); }
  };

  const isInWishlist = user?.wishlist?.includes(slug);

  if (loading) return (
    <div className="atelier-page atelier-product atelier-product--loading" aria-busy="true" aria-label={c.loadingProduct}>
      <div className="atelier-container atelier-product__grid">
        <Skeleton className="aspect-square rounded-3xl" />
        <div className="space-y-6">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-8 rounded-xl" />)}
        </div>
      </div>
    </div>
  );

  if (!product) return (
    <div className="atelier-page atelier-product atelier-product--empty">
      <div className="text-center">
        <p className="font-serif text-2xl text-foreground mb-4">{loadError ? c.temporarilyUnavailable : c.lost}</p>
        {loadError && <Button onClick={() => setRetry(value => value + 1)} className="mb-4 rounded-full">{c.retry}</Button>}
        <Button asChild variant="outline" className="rounded-full border-input bg-card px-6 font-sans hover:bg-secondary">
<Link to={lp('/boutique')}>{c.backToShop}</Link>
</Button>
      </div>
    </div>
  );

  // Chaque modèle se commande sur mesure. Certaines pièces sont déjà cousues : celles-là s'adoptent tout de suite.
  const isCustomizable = CUSTOM_ORDERS_ENABLED && product.custom_available !== false;
  const isAdopted = product.status === 'adopted';
  const canAdopt = !product.made_to_order && !isAdopted && product.stock > 0;
  const pieceStatus = isAdopted ? c.adopted : product.made_to_order
    ? c.toOrder : canAdopt ? c.available : c.unavailable;
  const adoptLabel = canAdopt ? c.adopt : pieceStatus;
  const isUnique = !product.made_to_order;
  const customPrice = product.custom_price ?? DEFAULT_CUSTOM_PRICE;
  const customShipsFree = customPrice >= FREE_SHIPPING_THRESHOLD;
  const productUrl = absoluteUrl(lp(`/produit/${product.slug}`));
  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: publicDescription(product.description),
    image: productMedia(product)[0]?.src ? absoluteUrl(productMedia(product)[0].src) : undefined,
    sku: product.slug,
    brand: { '@type': 'Brand', name: 'Le Placard à Doudous' },
    offers: {
      '@type': 'Offer',
      url: productUrl,
      priceCurrency: 'EUR',
      price: product.price,
      availability: canAdopt ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
    },
  };

  return (
    <div className="atelier-page atelier-product">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(productSchema) }} />
      <div className="atelier-container atelier-product__container">
        {/* Breadcrumb */}
        <nav aria-label={c.breadcrumb} className="atelier-product__breadcrumb">
          <Link to={lp('/boutique')}>{c.shop}</Link>
          <span aria-hidden="true">/</span>
          <span aria-current="page">{product.name}</span>
        </nav>

        <div className="atelier-product__grid">
          {/* Galerie — la photo domine, les vues secondaires en bandeau sous elle */}
          <div className="atelier-product__gallery">
            <ProductGallery product={product} transparent />
          </div>

          {/* Purchase box */}
          <div className="atelier-product__purchase">
            {/* Identité */}
            <div className="atelier-product__identity">
              <p className="atelier-product__eyebrow">{product.collection}</p>
              <h1>{product.name}</h1>
              <Button
                variant="outline"
                size="icon"
                onClick={handleWishlist}
                disabled={wishlistLoading}
                aria-pressed={Boolean(isInWishlist)}
                className="atelier-product__wishlist"
                aria-label={isInWishlist ? c.removeWishlist : c.addWishlist}
              >
                <Heart className={isInWishlist ? 'atelier-product__heart--saved' : ''} strokeWidth={1.5} aria-hidden="true" />
              </Button>
              <p className="atelier-product__subtitle">{c.subtitle}</p>
            </div>

            {/* Prix + statut */}
            <div className="atelier-product__price-row">
              <span className="atelier-product__price">{formatPrice(product.price)}</span>
              <span className={`atelier-product__piece-status ${isAdopted ? 'pastel-chip pastel--lilas' : canAdopt ? 'pastel-chip pastel--menthe' : ''}`}>{pieceStatus}</span>
              <span className="script-touch atelier-product__handmade">{c.handmade}</span>
            </div>

            {/* Achat de la pièce du registre */}
            <div className="atelier-product__inline-adopt">
              <Button
                onClick={handleAddToCart}
                disabled={!canAdopt}
                className="atelier-primary atelier-product__adopt bg-primary"
                data-testid="product-add-to-cart-button"
              >
                <ShoppingBag size={21} strokeWidth={1.5} aria-hidden="true" />
                {adoptLabel}
              </Button>
            </div>

            {isAdopted && (
              <p className="atelier-product__adopted-note">
                {c.adoptedNote}
              </p>
            )}

            {/* Le même modèle, cousu pour vous — sa propre carte atelier, pas un second bouton concurrent */}
            {isCustomizable && (
              <section className="atelier-product__custom" aria-label={c.customSection}>
                <div className="atelier-product__custom-heading">
                  <h2>
                    <span className="atelier-product__custom-title-desktop">{c.customTitle(product.name.split(' ')[0])}</span>
                    <span className="atelier-product__custom-title-mobile">{c.customTitleMobile}</span>
                  </h2>
                  <p className="atelier-product__custom-price">
                    {formatPrice(customPrice)}
                    <span className="sr-only">{c.allIncluded}</span>
                  </p>
                </div>
                <p className="atelier-product__custom-description">
                  {c.customDescription(product.name)}
                </p>
                <details className="atelier-product__custom-options">
                  <summary>{c.customOptions} <ChevronRight size={16} aria-hidden="true" /></summary>
                  <ul>
                    {c.customOptionLines.map(line => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                </details>
                <Button asChild variant="outline"
                  className="atelier-product__compose"
                  data-testid="product-custom-order-button"
                >
                  <Link to={`/sur-mesure?modele=${product.slug}`}>
                    <Pencil size={21} strokeWidth={1.5} aria-hidden="true" /> {c.designMine}
                  </Link>
                </Button>
                <p className="atelier-product__custom-delivery">
                  <Clock size={17} strokeWidth={1.5} aria-hidden="true" />
                  <span>{c.customDelivery(customShipsFree)}</span>
                </p>
              </section>
            )}

            {/* L'histoire de la pièce */}
            <div className="atelier-product__description">
              {String(publicDescription(product.description) || '').split(/\n\s*\n/).map(paragraph => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>

            <div className="atelier-product__details">
              <details>
                <summary><Leaf size={23} strokeWidth={1.5} aria-hidden="true" /> {c.materials} <ChevronRight size={17} aria-hidden="true" /></summary>
                <dl>
                  {[
                    [c.materials, product.material],
                    [c.care, product.care],
                    [c.madeIn, product.made_in],
                  ].map(([label, value]) => value && <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}
                </dl>
              </details>
              <details>
                <summary><Ruler size={23} strokeWidth={1.5} aria-hidden="true" /> {c.dimensions} <ChevronRight size={17} aria-hidden="true" /></summary>
                <dl>
                  {product.dimensions && <div><dt>{c.dimensions}</dt><dd>{product.dimensions}</dd></div>}
                  {isUnique && <div><dt>{c.piece}</dt><dd>{c.uniquePiece}</dd></div>}
                </dl>
              </details>
              <details>
                <summary><Truck size={23} strokeWidth={1.5} aria-hidden="true" /> {c.delivery} <ChevronRight size={17} aria-hidden="true" /></summary>
                <div className="atelier-product__delivery-details">
                  {[
                    { icon: Truck, text: c.deliveryText(formatPrice(FREE_SHIPPING_THRESHOLD)) },
                    { icon: RotateCcw, text: CUSTOM_ORDERS_ENABLED ? c.returnsCustom : c.returns },
                    { icon: Shield, text: c.secure },
                  ].map(({ icon: Icon, text }) => (
                    <div key={text}>
                      <Icon size={17} strokeWidth={1.5} aria-hidden="true" />
                      <p>{text}</p>
                    </div>
                  ))}
                </div>
              </details>
            </div>
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <section className="atelier-product__related" aria-labelledby="atelier-product-related-title">
            <h2 id="atelier-product-related-title">{c.related}</h2>
            <div className="atelier-product__related-list">
              {relatedProducts.slice(0, 3).map(p => (
                <ProductCardV2 key={p.id} product={p} catalogue />
              ))}
            </div>
            <Link to={lp('/boutique')} className="atelier-product__collection-link">{c.wholeCollection} <ArrowRight size={18} aria-hidden="true" /></Link>
          </section>
        )}
      </div>
      <div className="atelier-product__mobile-adopt" aria-label={c.adopt}>
        <span className="atelier-product__price">{formatPrice(product.price)}</span>
        <Button onClick={handleAddToCart} disabled={!canAdopt}
          className="atelier-primary atelier-product__adopt bg-primary" data-testid="product-add-to-cart-mobile-button">
          <ShoppingBag size={21} strokeWidth={1.5} aria-hidden="true" />
          {adoptLabel}
        </Button>
      </div>
    </div>
  );
}
